import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { LuSearch, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { CircleLoader } from "react-spinners";
import Button from "../../components/button";
import useRequest from "../../components/hooks/use-request";
import { showToast } from "../../components/toast";
import Icon from "../../assets/icons";

const DEFAULT_VALUE_PER_CODE = 20000;

const avatarColors = ["#0979A1", "#E85D04", "#7B2D8B", "#2D6A4F", "#D62828", "#023E8A"];
const avatarColor = (name: string) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const AllocateVoucher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recipientId } = useParams();
  const isHistory = !!recipientId; // read-only view of a recipient's voucher detail
  const userToken = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${userToken}` };

  // The leaderboard endpoint requires from/to. Inherit the range selected on the
  // Campaign page (passed via navigation state); fall back to Jan 1 2024 → today
  // so a direct visit/refresh still returns the full history.
  const { from, to } = useMemo(() => {
    const state = location.state as { from?: string; to?: string } | null;
    if (state?.from && state?.to) return { from: state.from, to: state.to };
    const now = new Date();
    const start = new Date(2024, 0, 1);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: iso(start), to: iso(now) };
  }, [location.state]);

  const [merchants, setMerchants] = useState<any[]>([]);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [voucherCount, setVoucherCount] = useState<number | "">("");
  const [valuePerCode, setValuePerCode] = useState<number | "">(DEFAULT_VALUE_PER_CODE);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recipient, setRecipient] = useState<any>(null);
  const [historyCodes, setHistoryCodes] = useState<{ code: string; used: boolean }[]>([]);
  const [emailSending, setEmailSending] = useState(false);

  const { makeRequest: getMerchants } = useRequest("/campaigns/merchants/leaderboard", "GET", headers);
  const { loading, makeRequest: allocate } = useRequest("/campaigns/vouchers/allocate", "POST", headers);
  const { makeRequest: getRecipient, loading: recipientLoading } = useRequest(
    `/campaigns/vouchers/recipient/${recipientId || ""}`,
    "GET",
    headers
  );

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { from, to };
      if (merchantSearch) params.search = merchantSearch;
      const [res] = await getMerchants(undefined, params);
      if (res?.data) setMerchants(res.data.leaderboard || []);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantSearch, from, to]);

  // History mode — load the recipient's voucher detail and populate read-only
  useEffect(() => {
    if (!isHistory) return;
    const fetch = async () => {
      const [res] = await getRecipient();
      const d = res?.data;
      if (!d) return;
      setRecipient(d);
      const unused = (d.codes?.unused || []).map((code: string) => ({ code, used: false }));
      const used = (d.codes?.used || []).map((code: string) => ({ code, used: true }));
      const all = [...unused, ...used];
      setHistoryCodes(all);
      setSelectedMerchant({
        merchantName: d.recipientName,
        merchantEmail: d.recipientEmail,
        userId: d.recipientId,
      });
      setVoucherCount(all.length);
      setValuePerCode(all.length > 0 ? Math.round((d.totalAllocated || 0) / all.length) : 0);
      setPreviewOpen(true);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]);

  const totalAmount = useMemo(
    () =>
      typeof voucherCount === "number" && typeof valuePerCode === "number"
        ? voucherCount * valuePerCode
        : 0,
    [voucherCount, valuePerCode]
  );

  const validityDays = useMemo(() => {
    if (!startDate || !endDate) return null;
    const diff = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.round(diff));
  }, [startDate, endDate]);

  const previewCodes = useMemo(
    () => Array.from({ length: typeof voucherCount === "number" ? voucherCount : 0 }, () => "VDS-XXXX-XXXX"),
    [voucherCount]
  );

  // In history mode the total comes straight from the recipient detail
  const displayTotal = isHistory ? recipient?.totalAllocated || 0 : totalAmount;

  const handleAllocate = async () => {
    if (!selectedMerchant) { showToast("Please select a merchant", false, { position: "top-center" }); return; }
    if (!voucherCount || voucherCount < 1) { showToast("Please enter number of voucher codes", false, { position: "top-center" }); return; }
    if (!valuePerCode || valuePerCode < 1) { showToast("Please enter a valid value per voucher code", false, { position: "top-center" }); return; }
    if (!startDate || !endDate) { showToast("Please select validity period", false, { position: "top-center" }); return; }

    const payload = {
      recipientType: "merchant",
      recipientId: selectedMerchant.userId ?? selectedMerchant.merchantId,
      numberOfCodes: voucherCount,
      valuePerCode,
      startDate,
      endDate,
    };
    const [res] = await allocate(payload);
    if (res?.status) {
      showToast(res.message || "Voucher allocated successfully", true, { position: "top-center" });
      navigate("/campaign", { state: { tab: "Voucher Management" } });
    } else {
      showToast(res?.message || "Failed to allocate voucher", false, { position: "top-center" });
    }
  };

  // Trigger a voucher email to the recipient. The endpoint is not available yet,
  // so this is stubbed to resolve successfully — swap in the real request below
  // when it exists, e.g. POST /campaigns/vouchers/recipient/:id/send-email.
  const handleSendEmail = async () => {
    const email = isHistory ? recipient?.recipientEmail : selectedMerchant?.merchantEmail;
    if (!email) {
      showToast("Select a recipient first", false, { position: "top-center" });
      return;
    }
    setEmailSending(true);
    try {
      // TODO: replace with the real endpoint once available
      await new Promise((resolve) => setTimeout(resolve, 800));
      showToast(`Voucher email sent to ${email}`, true, { position: "top-center" });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-6 w-[95%]">
        <button onClick={() => navigate(-1)} className="cursor-pointer">
          <Icon name="arrowLeft" />
        </button>
        <h2 className="text-[22px] font-bold">
          {isHistory ? "Voucher History" : "Allocate Voucher"}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-[95%]">
        {/* Left — Select Merchant (clear but non-interactive in history mode) */}
        <div
          className={`bg-white rounded-xl border border-gray-100 shadow-sm p-6 ${
            isHistory ? "pointer-events-none" : ""
          }`}>
          <h3 className="text-[16px] font-semibold">Select Merchant</h3>
          <p className="text-[13px] text-gray-500 mb-4">Choose merchant to allocate.</p>
          <div className="relative mb-4">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text"
              value={merchantSearch}
              onChange={(e) => setMerchantSearch(e.target.value)}
              placeholder="Search by merchant name/email"
              className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-[13px] outline-none focus:border-[#0979A1]"
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-[13px]">
            <thead className="bg-[#CFF0FC] h-10">
              <tr className="text-left">
                <th className="px-3 font-medium w-8"></th>
                <th className="px-3 font-medium">Rank</th>
                <th className="px-3 font-medium">Merchant</th>
                <th className="px-3 font-medium text-right">Allocated (₦)</th>
                <th className="px-3 font-medium text-right">Redeemed (₦)</th>
                <th className="px-3 font-medium text-right">Balance (₦)</th>
              </tr>
            </thead>
            <tbody>
              {merchants.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-gray-500">
                    No merchants found
                  </td>
                </tr>
              )}
              {merchants.map((m: any, i: number) => {
                const mId = m.userId ?? m.merchantId;
                return (
                <tr
                  key={mId || i}
                  className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${(selectedMerchant?.userId ?? selectedMerchant?.merchantId) === mId ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedMerchant(m)}>
                  <td className="px-3 py-2">
                    <input
                      type="radio"
                      readOnly
                      checked={(selectedMerchant?.userId ?? selectedMerchant?.merchantId) === mId}
                      className="accent-[#0979A1] w-4 h-4"
                    />
                  </td>
                  <td className="px-3 py-2">{m.rank ?? i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0" style={{ backgroundColor: avatarColor(m.merchantName || "") }}>
                        {m.merchantName?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[13px]">{m.merchantName}</p>
                        <p className="text-[11px] text-gray-500">{m.merchantEmail}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">{(m.voucherAllocated || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{(m.voucherUsed || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{m.voucherBalance != null ? (m.voucherBalance || 0).toLocaleString() : "-"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Right — Allocation Details */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-[16px] font-semibold">
            {isHistory ? "Voucher Details" : "Enter Allocation Details"}
          </h3>
          <p className="text-[13px] text-gray-500 mb-5">
            {isHistory
              ? recipientLoading
                ? "Loading voucher details..."
                : "Read-only view of this recipient's vouchers."
              : "Specify voucher amounts and allocation settings."}
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-1 block">Merchant</label>
              <input
                type="text"
                readOnly
                value={selectedMerchant?.merchantName || ""}
                placeholder="—"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] bg-gray-50 outline-none"
              />
            </div>

            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-1 block">Email Address</label>
              <input
                type="text"
                readOnly
                value={selectedMerchant?.merchantEmail || ""}
                placeholder="—"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] bg-gray-50 outline-none"
              />
              {selectedMerchant && <p className="text-[11px] text-gray-500 mt-1">Email notification will be sent to this email.</p>}
            </div>

            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-1 block">Number of Voucher Codes</label>
              <input
                type="number"
                min={1}
                readOnly={isHistory}
                value={voucherCount}
                onChange={(e) => setVoucherCount(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Select or type"
                className={`w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#0979A1] ${isHistory ? "bg-gray-50" : ""}`}
              />
              {typeof voucherCount === "number" && voucherCount > 0 && !isHistory && (
                <p className="text-[11px] text-gray-500 mt-1">You will be allocating {voucherCount} voucher code(s) to this merchant.</p>
              )}
            </div>

            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-1 block">Value per Voucher Code (₦)</label>
              <input
                type="number"
                min={1}
                readOnly={isHistory}
                value={valuePerCode}
                onChange={(e) => setValuePerCode(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Enter value per code"
                className={`w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#0979A1] ${isHistory ? "bg-gray-50" : ""}`}
              />
              <p className="text-[11px] text-gray-500 mt-1">Amount each voucher code is worth.</p>
            </div>

            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-1 block">Total Allocation Amount</label>
              <input
                type="text"
                readOnly
                value={displayTotal > 0 ? displayTotal.toLocaleString() : ""}
                placeholder="—"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] bg-gray-50 outline-none"
              />
              <p className="text-[11px] text-gray-500 mt-1">1 voucher code = ₦{(typeof valuePerCode === "number" ? valuePerCode : 0).toLocaleString()}</p>
            </div>

            {isHistory && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[13px] font-medium text-[#040821] mb-1 block">Total Redeemed (₦)</label>
                  <input
                    type="text"
                    readOnly
                    value={(recipient?.totalRedeemed || 0).toLocaleString()}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] bg-gray-50 outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[13px] font-medium text-[#040821] mb-1 block">Balance (₦)</label>
                  <input
                    type="text"
                    readOnly
                    value={(recipient?.balance || 0).toLocaleString()}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] bg-gray-50 outline-none"
                  />
                </div>
              </div>
            )}

            {/* Code Format Preview */}
            <div>
              <button
                type="button"
                onClick={() => setPreviewOpen((o) => !o)}
                className="flex items-center justify-between w-full text-[13px] font-medium text-[#040821] py-2">
                Code Format Preview
                {previewOpen ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
              </button>
              {previewOpen && isHistory && historyCodes.length > 0 && (
                <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                  {historyCodes.map((c, i) => (
                    <div key={i} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-[13px] bg-gray-50 flex items-center justify-between">
                      <span className="text-[#040821] font-medium tracking-wide">{c.code}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${c.used ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}>
                        {c.used ? "Used" : "Unused"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {previewOpen && !isHistory && previewCodes.length > 0 && (
                <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                  {previewCodes.map((code, i) => (
                    <div key={i} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-[13px] bg-gray-50 text-gray-400">
                      {code}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Validity Period */}
            <div>
              <label className="text-[13px] font-medium text-[#040821] mb-2 block">Voucher Validity Period</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="date"
                    disabled={isHistory}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0979A1] ${isHistory ? "bg-gray-50" : ""}`}
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="date"
                    disabled={isHistory}
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0979A1] ${isHistory ? "bg-gray-50" : ""}`}
                  />
                </div>
              </div>
              {validityDays !== null && <p className="text-[11px] text-gray-500 mt-1">{validityDays} days</p>}
            </div>

            <button
              onClick={handleAllocate}
              disabled={loading || isHistory}
              className="w-full bg-[#0979A1] text-white rounded-lg py-3 text-[15px] font-semibold hover:bg-[#076a8a] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2">
              {loading ? (
                <CircleLoader color="#ffffff" loading size={18} />
              ) : isHistory ? (
                "View Only"
              ) : (
                "Confirm & Allocate Voucher"
              )}
            </button>

            <button
              onClick={handleSendEmail}
              disabled={emailSending}
              className="w-full border border-[#0979A1] text-[#0979A1] rounded-lg py-3 text-[15px] font-semibold hover:bg-[#E8F7FA] disabled:opacity-60 flex items-center justify-center gap-2 mt-3">
              {emailSending ? (
                <CircleLoader color="#0979A1" loading size={18} />
              ) : (
                "Send Voucher Email to Recipient"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllocateVoucher;
